package services.http

import it.neckar.lizergy.PlannerClientDeploymentZone
import it.neckar.lizergy.PlannerServiceDeploymentZone
import it.neckar.lizergy.model.configuration.PhotovoltaicsConfiguration.PhotovoltaicsConfigurationId
import it.neckar.lizergy.model.configuration.quote.QuoteSnapshot
import it.neckar.lizergy.model.configuration.quote.QuoteSnapshot.QuoteSnapshotId
import it.neckar.lizergy.model.http.PlannerEndpoints
import it.neckar.lizergy.model.http.PlannerEndpoints.Auth
import it.neckar.lizergy.model.http.PlannerEndpoints.Comments
import it.neckar.lizergy.model.http.PlannerEndpoints.ProjectQuery
import it.neckar.lizergy.model.price.PriceListExportCategory
import it.neckar.lizergy.model.project.ProjectConfiguration.PhotovoltaicsProjectId
import it.neckar.open.http.Url
import it.neckar.open.http.UrlPattern
import it.neckar.projects.common.Port
import it.neckar.projects.pvplanner.PvPlannerPorts
import it.neckar.projects.pvplanner.PvPlannerService
import kotlin.uuid.Uuid

/**
 * Calculates the urls for the services.
 *
 * Does include the hostnames and ports.
 *
 * Uses [PlannerEndpoints] to calculate the paths.
 */
class PlannerUrlSupport internal constructor(
  /**
   * The usage type
   */
  val usage: Usage,

  /**
   * Creates a URL for a relative path
   */
  val createUrl: (service: PvPlannerService, relativePath: Url.Relative, portForDev: Port) -> Url.Absolute,
) {

  fun createUrl(service: PvPlannerService, relativePath: UrlPattern.Relative0, portForDev: Port): Url.Absolute {
    return createUrl(service, relativePath.resolve(), portForDev)
  }

  /**
   * Urls to the authentication service
   */
  val auth: AuthServiceUrls = AuthServiceUrls()

  inner class AuthServiceUrls : ServiceUrls {
    override val service: PvPlannerService = PvPlannerService.AuthService

    override val portForDev: Port = PvPlannerPorts.authService
    override val heartbeat: Url.Absolute = createUrl(service, Auth.heartbeatUrl, portForDev)
    override val baseUrl: Url.Absolute = createUrl(service, Auth.baseUrl, portForDev)

    val login: Url.Absolute = createUrl(service, Auth.loginUrl(), portForDev)
    val refreshAccessToken: Url.Absolute = createUrl(service, Auth.refreshAccessTokenUrl(), portForDev)

    val changePassword: Url.Absolute = createUrl(service, Auth.ChangePassword, portForDev)
    val addNewUser: Url.Absolute = createUrl(service, Auth.AddNewUser, portForDev)
    val changeUserInfo: Url.Absolute = createUrl(service, Auth.ChangeUserInfo, portForDev)
    val deleteUser: Url.Absolute = createUrl(service, Auth.DeleteUser, portForDev)
    val addNewCompany: Url.Absolute = createUrl(service, Auth.AddNewCompany, portForDev)
    val changeCompanyInfo: Url.Absolute = createUrl(service, Auth.ChangeCompanyInfo, portForDev)
    val listUsersAndCompanies: Url.Absolute = createUrl(service, Auth.ListUsersAndCompanies, portForDev)

  }

  val pdf: PdfServiceUrls = PdfServiceUrls()

  inner class PdfServiceUrls : ServiceUrls {
    override val service: PvPlannerService = PvPlannerService.PdfReportService

    override val portForDev: Port = PvPlannerPorts.pdfReportService
    override val heartbeat: Url.Absolute = createUrl(service, PlannerEndpoints.PdfReport.heartbeatUrl, portForDev)
    override val baseUrl: Url.Absolute = createUrl(service, PlannerEndpoints.PdfReport.baseUrl, portForDev)

    fun quoteOfferForCurrent(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.quoteOfferForCurrentUrl(projectId, configurationId), portForDev)
    }

    fun quoteOfferForSnapshot(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.quoteOfferForSnapshotUrl(projectId, configurationId, quoteSnapshotId), portForDev)
    }

    fun orderConfirmationForSnapshot(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.orderConfirmationForSnapshotUrl(projectId, configurationId, quoteSnapshotId), portForDev)
    }

    fun stringPlan(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.stringPlanUrl(projectId, configurationId), portForDev)
    }

    fun assemblyPortfolio(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.assemblyPortfolioUrl(projectId, configurationId), portForDev)
    }

    fun assemblyPortfolioRoof(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.assemblyPortfolioRoofUrl(projectId, configurationId), portForDev)
    }

    fun assemblyPortfolioBasement(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.assemblyPortfolioBasementUrl(projectId, configurationId), portForDev)
    }

    fun documentation(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfReport.documentationUrl(projectId, configurationId), portForDev)
    }
  }

  val pdfPreviewService: PdfPreviewServiceUrls = PdfPreviewServiceUrls()

  inner class PdfPreviewServiceUrls : ServiceUrls {
    override val service: PvPlannerService = PvPlannerService.PdfPreviewService

    override val portForDev: Port = PvPlannerPorts.pdfPreviewService
    override val heartbeat: Url.Absolute = createUrl(service, PlannerEndpoints.PdfPreview.heartbeatUrl, portForDev)
    override val baseUrl: Url.Absolute = createUrl(service, PlannerEndpoints.PdfPreview.baseUrl, portForDev)

    /**
     * Creates the URL for an offer preview based on an existing quote snapshot
     */
    fun quoteOfferPreview(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.quoteOfferForSnapshotUrl(projectId, configurationId, quoteSnapshotId) + "/index.html", portForDev)
    }

    /**
     * Creates the URL for a confirmation preview based on a configuration - without a [QuoteSnapshot]. Returns the values for "current"
     */
    fun quoteOfferPreviewCurrent(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.quoteOfferForCurrentUrl(projectId, configurationId) + "/index.html", portForDev)
    }

    fun quoteConfirmationPreview(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, quoteSnapshotId: QuoteSnapshotId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.orderConfirmationForSnapshotUrl(projectId, configurationId, quoteSnapshotId) + "/index.html", portForDev)
    }

    fun stringPlan(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.stringPlanUrl(projectId, configurationId) + "/index.html", portForDev)
    }

    fun assemblyPortfolio(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.assemblyPortfolioUrl(projectId, configurationId) + "/index.html", portForDev)
    }

    fun assemblyPortfolioRoof(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.assemblyPortfolioRoofUrl(projectId, configurationId) + "/index.html", portForDev)
    }

    fun assemblyPortfolioBasement(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.assemblyPortfolioBasementUrl(projectId, configurationId) + "/index.html", portForDev)
    }

    fun documentation(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, PlannerEndpoints.PdfPreview.documentationUrl(projectId, configurationId) + "/index.html", portForDev)
    }
  }


  val projectQuery: ProjectQueryServiceUrls = ProjectQueryServiceUrls()

  inner class ProjectQueryServiceUrls : ServiceUrls {
    override val service: PvPlannerService = PvPlannerService.ProjectQueryService

    override val portForDev: Port = PvPlannerPorts.projectQueryService
    override val heartbeat: Url.Absolute = createUrl(service, ProjectQuery.heartbeatUrl, portForDev)
    override val baseUrl: Url.Absolute = createUrl(service, ProjectQuery.baseUrl, portForDev)

    val projectPreviewsUrl: Url.Absolute = createUrl(service, ProjectQuery.ProjectPreviews, portForDev)
    val projectCountsUrl: Url.Absolute = createUrl(service, ProjectQuery.ProjectCounts, portForDev)
    val accountingProjectPreviewsUrl: Url.Absolute = createUrl(service, ProjectQuery.AccountingProjectPreviews, portForDev)
    val accountingProjectPreviewsWithRevenuesUrl: Url.Absolute = createUrl(service, ProjectQuery.AccountingProjectPreviewsWithRevenues, portForDev)
    val allAccountingProjectPreviewsAsCSVUrl: Url.Absolute = createUrl(service, ProjectQuery.AllAccountingProjectPreviewsAsCSV, portForDev)


    fun staticPriceList(): Url.Absolute {
      return createUrl(service, ProjectQuery.StaticPriceList, portForDev)
    }

    fun priceListExport(category: PriceListExportCategory): Url.Absolute {
      return createUrl(service, ProjectQuery.priceListExportUrl(category), portForDev)
    }

    fun accountingProjectPreviewsExport(): Url.Absolute {
      return createUrl(service, ProjectQuery.AccountingProjectPreviewsExport, portForDev)
    }

    fun projectPreviewsExport(): Url.Absolute {
      return createUrl(service, ProjectQuery.ProjectPreviewsExport, portForDev)
    }

    /**
     * Returns the projects including preview values.
     * Returns all projects for *all* companies the user has access to
     */
    fun projects(): Url.Absolute {
      return createUrl(service, ProjectQuery.projectsUrl, portForDev)
    }

    fun accountingProjects(): Url.Absolute {
      return createUrl(service, ProjectQuery.AccountingProjects, portForDev)
    }

    fun projectsSearch(): Url.Absolute {
      return createUrl(service, ProjectQuery.ProjectsSearch, portForDev)
    }

    /**
     * The base URL for a project
     */
    fun project(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return createUrl(service, ProjectQuery.projectUrl(projectId), portForDev)
    }

    fun resolvedProject(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return createUrl(service, ProjectQuery.resolvedProjectUrl(projectId), portForDev)
    }

    fun multipleResolvedProjects(): Url.Absolute {
      return createUrl(service, ProjectQuery.multipleResolvedProjectsUrl, portForDev)
    }

    fun archiveProjectUrl(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return createUrl(service, ProjectQuery.archiveProjectUrl(projectId), portForDev)
    }

    fun blueprint(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.blueprint.resolve()
    }

    fun verifications(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.verifications.resolve()
    }

    fun orderSpecialMaterial(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.orderSpecialMaterial.resolve()
    }

    fun gridAssessment(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.gridAssessment.resolve()
    }

    fun assemblyPortfolio(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.assemblyPortfolio.resolve()
    }

    fun advanceInvoice(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.advanceInvoice.resolve()
    }

    fun assemblyRoof(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.assemblyRoof.resolve()
    }

    fun assemblyBasement(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.assemblyBasement.resolve()
    }

    fun switchMeterBox(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.switchMeterBox.resolve()
    }

    fun startupOperations(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.startupOperations.resolve()
    }

    fun finishingUp(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.finishingUp.resolve()
    }

    fun finalAccount(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.finalAccount.resolve()
    }

    fun documentation(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return project(projectId) + ProjectQuery.documentation.resolve()
    }


    /**
     * Returns all configurations for a project
     */
    fun configurationsForProject(projectId: PhotovoltaicsProjectId): Url.Absolute {
      return createUrl(service, ProjectQuery.configurationsForProjectUrl(projectId), portForDev)
    }

    fun configuration(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, ProjectQuery.configurationUrl(projectId, configurationId), portForDev)
    }

    fun quoteConfiguration(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, ProjectQuery.quoteConfigurationUrl(projectId, configurationId), portForDev)
    }

    fun moduleLayouts(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return configuration(projectId, configurationId) + ProjectQuery.moduleLayouts.resolve()
    }

    fun quoteSnapshots(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return configuration(projectId, configurationId) + ProjectQuery.quoteSnapshots.resolve()
    }

    /**
     * Creates the url to a specific quote snapshot
     */
    fun quoteSnapshot(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId, snapshotId: QuoteSnapshotId): Url.Absolute {
      return createUrl(service, ProjectQuery.quoteSnapshotUrl(projectId, configurationId, snapshotId), portForDev)
    }

    fun earningsDistribution(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, ProjectQuery.earningsDistributionUrl(projectId, configurationId), portForDev)
    }

    fun manualQuoteElements(projectId: PhotovoltaicsProjectId, configurationId: PhotovoltaicsConfigurationId): Url.Absolute {
      return createUrl(service, ProjectQuery.manualQuoteElementsUrl(projectId, configurationId), portForDev)
    }


    fun processStates(uuid: Uuid): Url.Absolute {
      return createUrl(service, ProjectQuery.processStatesForComponentUrl(uuid), portForDev)
    }

    fun multipleProcessStates(): Url.Absolute {
      return createUrl(service, ProjectQuery.addMultipleProcessStates, portForDev)
    }

    fun duplicateProcessStates(): Url.Absolute {
      return createUrl(service, ProjectQuery.duplicateProcessStates, portForDev)
    }

    fun reassignProcessStates(): Url.Absolute {
      return createUrl(service, ProjectQuery.reassignProcessStates, portForDev)
    }

    fun allProcessStates(): Url.Absolute {
      return createUrl(service, ProjectQuery.allProcessStatesUrl(), portForDev)
    }
  }


  val geoInformation: GeoInformationServiceUrls = GeoInformationServiceUrls()

  inner class GeoInformationServiceUrls : ServiceUrls {
    override val service: PvPlannerService = PvPlannerService.GeoInformationService


    override val portForDev: Port = PvPlannerPorts.geoInformationService
    override val heartbeat: Url.Absolute = createUrl(service, PlannerEndpoints.GeoInformation.heartbeatUrl, portForDev)
    override val baseUrl: Url.Absolute = createUrl(service, PlannerEndpoints.GeoInformation.baseUrl, portForDev)

    val geoCodeServiceUrl: Url.Absolute = createUrl(service, PlannerEndpoints.GeoInformation.GeoCode, portForDev)

    /**
     * URL for the geoInformation service OSRM
     */
    val geoInformationServiceUrlOSRM: Url.Absolute = createUrl(service, PlannerEndpoints.GeoInformation.GeoRoute, portForDev)
  }


  val comments: CommentsServiceUrls = CommentsServiceUrls()

  inner class CommentsServiceUrls : ServiceUrls {
    override val service: PvPlannerService = PvPlannerService.CommentsService

    override val portForDev: Port = PvPlannerPorts.commentsService
    override val heartbeat: Url.Absolute = createUrl(service, Comments.heartbeatUrl, portForDev)
    override val baseUrl: Url.Absolute = createUrl(service, Comments.baseUrl, portForDev)

    fun allComments(): Url.Absolute {
      return createUrl(service, Comments.AllComments, portForDev)
    }

    fun comments(uuid: Uuid): Url.Absolute {
      return createUrl(service, Comments.commentsForComponentUrl(uuid), portForDev)
    }

    fun duplicateComments(): Url.Absolute {
      return createUrl(service, Comments.DuplicateComments, portForDev)
    }
  }

  /**
   * Returns the service urls
   */
  fun getForService(service: PvPlannerService): ServiceUrls {
    return when (service) {
      PvPlannerService.AuthService -> auth
      PvPlannerService.CommentsService -> comments
      PvPlannerService.GeoInformationService -> geoInformation
      PvPlannerService.PdfPreviewService -> pdfPreviewService
      PvPlannerService.PdfReportService -> pdf
      PvPlannerService.ProjectQueryService -> projectQuery
    }
  }

  companion object {
    fun forClient(
      /**
       * The current deployment zone
       */
      deploymentZone: PlannerClientDeploymentZone,

      /**
       * Creates a URL for a relative path.
       * Should only be provided for tests
       */
      createUrl: (relativePath: Url.Relative, portForDev: Port) -> Url.Absolute = { relativePath, portForDev ->
        calculateBaseUrlForUsageInClient(deploymentZone, portForDev) + relativePath
      },

      ): PlannerUrlSupport {
      return PlannerUrlSupport(Usage.WebClient) { service, relativePath, portForDev ->
        createUrl(relativePath, portForDev)
      }
    }

    fun forService(
      /**
       * The current deployment zone
       */
      deploymentZone: PlannerServiceDeploymentZone,
      createUrl: (service: PvPlannerService, relativePath: Url.Relative, portForDev: Port) -> Url.Absolute = { service, relativePath, portForDev ->
        calculateBaseUrlForUsageInService(deploymentZone, service, portForDev) + relativePath
      },
    ): PlannerUrlSupport {
      return PlannerUrlSupport(Usage.Service) { service: PvPlannerService, relativePath: Url.Relative, portForDev: Port ->
        createUrl(service, relativePath, portForDev)
      }
    }

    /**
     * Calculates the base URL (hostname + port)
     */
    fun calculateBaseUrl(usage: Usage, deploymentZone: PlannerClientDeploymentZone?, serviceDeploymentZone: PlannerServiceDeploymentZone?, service: PvPlannerService, portForDev: Port): Url.Absolute {
      return when (usage) {
        Usage.WebClient -> calculateBaseUrlForUsageInClient(requireNotNull(deploymentZone), portForDev)
        Usage.Service -> calculateBaseUrlForUsageInService(requireNotNull(serviceDeploymentZone), service, portForDev)
      }
    }

    /**
     * Returns the base URL - depending on the environment (dev or production)
     *
     * Returns an absolute URL with a trailing "/"
     */
    fun calculateBaseUrlForUsageInClient(
      deploymentZone: PlannerClientDeploymentZone,
      portForDev: Port,
    ): Url.Absolute {
      val baseUrl = when (deploymentZone) {
        /**
         * Use the local development services - with different ports
         */
        PlannerClientDeploymentZone.LocalDevelopmentDocker, PlannerClientDeploymentZone.LocalDevelopment -> Url.absolute("http://localhost:$portForDev/")

        /**
         * Uses nginx to proxy the requests to the correct service (on localhost)
         */
        PlannerClientDeploymentZone.Production -> Url.absolute(("https://pv.neckar.it/"))
        PlannerClientDeploymentZone.Schaufenster -> Url.absolute(("https://schaufenster.pv.neckar.it/"))
        PlannerClientDeploymentZone.Spielwiese -> Url.absolute(("https://spielwiese.pv.neckar.it/"))
      }

      return baseUrl.also {
        require(it.value.endsWith("/")) {
          "baseURI $it does not end with '/' but should!"
        }
      }
    }

    /**
     * Creates the URLs for usage in services (to access other services)
     */
    @Suppress("HttpUrlsUsage")
    fun calculateBaseUrlForUsageInService(
      deploymentZone: PlannerServiceDeploymentZone,
      service: PvPlannerService,
      portForDev: Port,
    ): Url.Absolute {
      return when (deploymentZone) {
        PlannerServiceDeploymentZone.LocalDevelopment -> Url.absolute("http://localhost:$portForDev")
        PlannerServiceDeploymentZone.Docker -> Url.absolute("http://${service.serviceName}:$portForDev")
      }
    }
  }

  /**
   * The usage type of the [PlannerUrlSupport]
   */
  enum class Usage {
    /**
     * For usage in the web client (react)
     */
    WebClient,

    /**
     * For usage in the microservices (on the server)
     */
    Service
  }

  interface ServiceUrls {
    val service: PvPlannerService

    /**
     * The port used for dev connections
     */
    val portForDev: Port

    /**
     * The URL for the heartbeat
     */
    val heartbeat: Url

    /**
     * The base url for the service
     */
    val baseUrl: Url
  }
}
